import type { RequirementKind as ModelRequirementKind, RequirementStatus, SectionContent } from "@the-real-insight/tri-model";
/**
 * Client der Richtlinien-Werkstatt — /:customer/guidelines auf tri-server.
 *
 * Lesen: technische Regeln, die der Mandant sieht, mit Abschnitten, Kanten ins Recht, Belegstellen und
 * erkannten Anforderungen; die Prüfliste nach Rechtsänderungen. Schreiben (Kante prüfen, Entwurf
 * speichern, Abschnitt anlegen oder entfallen lassen) nach Rolle im verantwortenden Arbeitskreis, nur im
 * Mandanten des Rechteinhabers — der Server antwortet sonst 403.
 */

import { configuration } from "../Configuration";
import { ApplicationError, UnauthorizedError } from "../general/Errors";
import { authenticatedFetch } from "../JWTAuthService";

export type LinkStatus = "proposed" | "confirmed" | "rejected";
/** Verbindlichkeit wie im Modell (`DocumentRequirement.kind`). */
export type RequirementKind = `${ModelRequirementKind}`;

export interface Span {
  start: number;
  end: number;
}

/** Was der Nutzer an einer Richtlinie darf — aus seiner Rolle im verantwortenden Arbeitskreis. */
export interface GuidelinePermissions {
  /** Kommentieren und Änderungen vorschlagen — jedes Mitglied des Rechteinhabers. */
  comment: boolean;
  editText: boolean;
  editStructure: boolean;
  reviewLinks: boolean;
  approve: boolean;
}

export interface WorkingGroupRef {
  key: string;
  name: string;
}

export type GlossaryKind = "term" | "abbreviation";
export type GlossaryOccurrenceState = "same" | "reworded" | "different";
export type GlossaryEntryState = "consistent" | "different" | "single" | "glossaryOnly";

export interface GlossaryTermRecord {
  key: string;
  term: string;
  kind: GlossaryKind;
  definition: string;
  by?: string;
  at?: string;
}

export interface GlossaryOccurrence {
  documentId: string;
  guideline: string;
  sectionId: string;
  number: string | null;
  term: string;
  qualifier: string | null;
  kind: GlossaryKind;
  definition: string;
  /** Kennung des Definitionsabsatzes — Ziel eines Begriffsverweises. */
  element: string | null;
  draft: boolean;
  state: GlossaryOccurrenceState | null;
}

export interface GlossaryEntry {
  key: string;
  term: string;
  kind: GlossaryKind;
  glossary: GlossaryTermRecord | null;
  reference: string | null;
  occurrences: GlossaryOccurrence[];
  state: GlossaryEntryState;
}

export interface GlossaryView {
  entries: GlossaryEntry[];
  counts: { total: number; different: number; consistent: number; single: number; glossaryOnly: number; defined: number };
  canEdit: boolean;
}

export type GuidelineNeed = "revise" | "review" | "current";

export interface GuidelineDashboardItem {
  documentId: string;
  /** Überarbeitung (Recht/Norm geändert), Prüfung (Erkanntes offen, Glossar), aktuell. */
  need: GuidelineNeed;
  reasons: string[];
  title: string;
  edition: string | null;
  ageYears: number | null;
  workingGroup: WorkingGroupRef | null;
  sections: number;
  drafts: number;
  openSuggestions: number;
  openDiscussions: number;
  requirements: number;
  uncheckedRequirements: number;
  linksConfirmed: number;
  linksProposed: number;
  law: { since: string | null; changedRegulations: number; changes: number; otherChanges: number; newerEditions: number; outsideCorpus: number; ok: boolean };
  glossaryDeviations: number;
  newEdition: { label: string | null; status: string; at: string | null } | null;
}

export interface GuidelineDashboard {
  items: GuidelineDashboardItem[];
  totals: { guidelines: number; averageAge: number | null; changedRegulations: number; openWork: number; uncheckedRequirements: number; linksProposed: number; glossaryDeviations: number; awaitingApproval: number; need: Record<GuidelineNeed, number> };
}

/** Vorlage einer neuen Richtlinie: Standardgerüst, Gliederung oder Gliederung mit Inhalt einer bestehenden. */
export type NewGuidelineTemplate = "standard" | "outline" | "copy";

export interface GuidelineSummary {
  _id: string;
  title: string;
  date?: string;
  sectionCount: number;
  edition: string | null;
  superseded: boolean;
  workingGroup: WorkingGroupRef | null;
  permissions: GuidelinePermissions;
  writable: boolean;
}

export interface GuidelineSection {
  _id: string;
  /** Inhalt liegt als Blockmodell vor (laden über `getSectionContent`). */
  hasBlocks?: boolean;
  number: string;
  /** „app“: Anhang — Nummer „A“, gezeigt als „Anhang A“. */
  kind?: string | null;
  title: string;
  level: number;
  order: number;
  parent: string | null;
  pageFrom?: number;
  pageTo?: number;
  text: string;
  /** Entwurf: geänderter Text, neu angelegter (`isNew`) oder entfallender (`removed`) Abschnitt. */
  draft: {
    text: string | null;
    /** Plate-Inhalt mit Kommentar- und Vorschlagsmarken, sobald im Editor gespeichert. */
    value?: unknown[] | null;
    openSuggestions?: number;
    at: string;
    isNew: boolean;
    removed: boolean;
  } | null;
  requirements: Array<Span & { kind: RequirementKind }>;
  unresolvedReferences: string[];
}

export interface GuidelineLink {
  _id: string;
  section: string;
  role: string;
  roleLabel: string;
  targetType: string;
  label: string;
  regulationTitle?: string;
  citedAs: string | null;
  confidence: number | null;
  status: LinkStatus;
  origin: string;
  extractor: string | null;
  evidence: string | null;
  span: Span | null;
  /** Ziel im Recht (bei Pflichten die Vorschrift der Pflicht); fehlt bei Objektkanten. */
  target?: { regulationId: string; provisionId: string | null } | null;
  /** Amtliche Seite der Vorschrift bzw. des Regelwerks, bei lizenzierten Normen der Herausgeber. */
  officialUrl?: string | null;
  officialLabel?: string | null;
  /** Freier Wortlaut liegt vor (nur frei veröffentlichte Regelwerke). */
  hasText?: boolean;
  /** Kante auf das ganze Regelwerk: dessen Vorschriften lassen sich durchblättern (nur frei veröffentlichte). */
  hasProvisions?: boolean;
}

/** Eine verknüpfte Vorschrift zum Nachlesen. */
export interface ProvisionView {
  regulationId: string;
  regulationTitle: string | null;
  shortName: string | null;
  provisionId: string | null;
  heading: string;
  title: string | null;
  repealed: boolean;
  text: string | null;
  licensed: boolean;
  officialUrl: string | null;
  officialLabel: string | null;
  /** Ohne Vorschrift: Inhaltsverzeichnis des Regelwerks (leer bei lizenzierten). */
  provisions?: { provisionId: string; title: string | null }[];
}

export interface DiscussionComment {
  id: string;
  name: string;
  by: string;
  body: string;
  at: string;
}

/** Ein Kommentar-Faden, verankert über die Marke `comment_<discussionId>` im Abschnittstext. */
export interface Discussion {
  discussionId: string;
  section: string;
  quote: string;
  /** Kennung des Absatzes, an dem der Faden hängt (Anker). */
  element?: string | null;
  resolved: boolean;
  createdAt: string;
  comments: DiscussionComment[];
}

export type ChangeClass = "unchanged" | "reworded" | "changed" | "new" | "removed";

/** Ein Abschnitt im Plan der nächsten Ausgabe: alte und neue Nummer, Einstufung, Texte (nur bei Änderung). */
export interface EditionPlanEntry {
  sectionId: string;
  oldNumber: string | null;
  newNumber: string | null;
  title: string;
  level: number;
  change: ChangeClass;
  text: string | null;
  oldText: string | null;
  parent: string | null;
}

export interface EditionSummary {
  unchanged: number;
  reworded: number;
  changed: number;
  new: number;
  removed: number;
  renumbered: number;
}

export interface EditionRecord {
  _id: string;
  label: string;
  status: "submitted" | "approved" | "rejected" | "withdrawn";
  submittedAt: string;
  decidedAt: string | null;
  comment: string | null;
  summary: EditionSummary;
  newDocument: string | null;
}

export interface EditionState {
  plan: EditionPlanEntry[];
  summary: EditionSummary;
  open: EditionRecord | null;
  history: EditionRecord[];
  permissions: GuidelinePermissions;
}

export interface GuidelineWorkspace {
  document: {
    _id: string;
    title: string;
    date?: string;
    edition: string | null;
    previousEdition: string | null;
    supersededBy: { _id: string; edition: string } | null;
    openEdition: { _id: string; label: string; submittedAt: string } | null;
    /** Nur die Originalausgabe hat ein PDF; Ausgaben aus der Werkstatt gibt es als Text. */
    hasPdf: boolean;
    workingGroup: WorkingGroupRef | null;
    permissions: GuidelinePermissions;
    writable: boolean;
  };
  sections: GuidelineSection[];
  links: GuidelineLink[];
}

const processResponse = async (response: Response) => {
  if (response.ok) {
    const text = await response.text();

    return text ? JSON.parse(text) : null;
  }

  if (response.status === 401) {
    throw new UnauthorizedError();
  }

  let errorMessage = `${response.status} ${response.statusText}`;

  try {
    const errorData = await response.json();
    errorMessage = errorData.message || errorData.error || errorMessage;
  } catch {
    // response body not JSON — keep default message
  }

  throw new ApplicationError(errorMessage, response.status);
};

/** Eine Anforderung als Objekt: feste Kennung, Zerlegung in die Begriffe der Pflichten. */
export interface StoredRequirement {
  key: string;
  text: string;
  kind: RequirementKind;
  /** Adressat aus dem Zusammenhang, wo der Satz keinen nennt (Passiv). */
  impliedAddressee?: string | null;
  impliedRoleCode?: string | null;
  status: Exclude<`${RequirementStatus}`, "retired">;
  anchor: { element: string; from: number; to: number; quote: string };
  addressee: string | null;
  roleCode: string | null;
  subject: string | null;
  regulatedObjectCode: string | null;
  action: string | null;
  interval: { months?: number; trigger?: string; text?: string } | null;
  obligationType: string | null;
  modality: string | null;
  obligationKeys: string[];
  since: string | null;
  changedIn: string | null;
  decomposed: boolean;
}

export interface SectionRequirements {
  items: StoredRequirement[];
  /** Was der Entwurf ändern würde — erst mit der Freigabe gespeichert. */
  draft: { changed: { key: string; text: string }[]; created: { text: string; kind: RequirementKind }[]; retired: string[] } | null;
}

/** Fassungen eines Abschnitts, neueste zuerst, und der aktuelle Stand zum Vergleichen. */
export interface SectionVersions {
  rev: number | null;
  current: string;
  hasDraft: boolean;
  items: { rev: number; origin: "import" | "session" | "manual" | "restore"; label: string | null; authors: string[]; at: string; restoredFrom: number | null }[];
}

/** Inhalt eines Abschnitts im Blockmodell: geltende Fassung und Entwurf. */
export interface SectionBlocks {
  blocks: SectionContent["blocks"] | null;
  footnotes: NonNullable<SectionContent["footnotes"]>;
  rev: number | null;
  draft: SectionContent | null;
}

/** Ein Abschnitt als NISO-STS-XML und seine Blöcke — zum Prüfen der Aufbereitung. */
export interface SectionStructure {
  hasBlocks: boolean;
  rev: number | null;
  xml: string;
  blocks: unknown[];
  footnotes: unknown[];
}

export type ExportFormat = "docx" | "pdf" | "sts";

export interface InboxItem {
  documentId: string;
  documentTitle: string;
  kind: "edition" | "suggestions" | "requirements" | "links" | "comments";
  count: number;
  section?: string | null;
  label?: string;
  at?: string | null;
}

export interface GlobalSearchSection {
  _id: string;
  documentId: string;
  documentTitle: string;
  number: string;
  title: string;
  text: string;
}

export interface DocxImportResult {
  documentId: string;
  title: string | null;
  sections: number;
  images: { stored: number; skipped: string[] };
  stats: { paragraphs: number; tables: number; figures: number; footnotes: number; lists: number };
}

/**
 * Ob der Mandant die Werkstatt im Menü sieht: am Marktplatz-Eintrag freigegeben oder gekauft (Server
 * `…/guidelines/access`). Ohne Antwort `null` — die Seitenleiste blendet dann nichts aus oder ein.
 */
export const fetchGuidelineAccess = async (customer: string): Promise<boolean | null> => {
  try {
    const response = await authenticatedFetch(`${configuration.serverUrl}/${customer}/guidelines/access`, { method: "GET" });
    if (!response.ok) return null;
    return !!(await response.json())?.visible;
  } catch {
    return null;
  }
};

/** Antwort auf „Abschnitt entwerfen“: gesetzte Vorschläge und die Quellen, auf die sich der Entwurf stützt. */
export interface SectionDraftProposal {
  suggestions: number;
  openSuggestions: number;
  live: boolean;
  sources: { key: string; citation: string }[];
  /** Aussagen ohne (ausreichenden) Beleg, die zu offenen Fragen an den Arbeitskreis wurden. */
  demoted: number;
}

export const useGuidelinesAPI = (customer: string) => {
  const baseUrl = `${configuration.serverUrl}/${customer}/guidelines`;

  const request = async (method: "GET" | "PATCH" | "POST" | "DELETE", path: string, body?: unknown) => {
    const response = await authenticatedFetch(`${baseUrl}${path}`, {
      method,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      headers: { "Content-Type": "application/json" },
    });

    return processResponse(response);
  };

  return {
    listGuidelines: async (): Promise<GuidelineSummary[]> => (await request("GET", ""))?.items || [],
    /** Liste und ob der Nutzer neue Richtlinien aus Word anlegen darf (Geschäftsstelle, Administration). */
    getGuidelineIndex: async (): Promise<{ items: GuidelineSummary[]; canImport: boolean; workingGroups: WorkingGroupRef[]; canSeeDashboard: boolean }> => {
      const r = await request("GET", "");
      return { items: r?.items || [], canImport: !!r?.canImport, workingGroups: r?.workingGroups || [], canSeeDashboard: !!r?.canSeeDashboard };
    },
    /** Glossar des Mandanten mit Abgleich; mit `documentId` nur Begriffe dieser Richtlinie. */
    getGlossary: (documentId?: string): Promise<GlossaryView> => request("GET", `/glossary${documentId ? `?document=${encodeURIComponent(documentId)}` : ""}`),
    setGlossaryTerm: (input: { term: string; definition: string; kind?: GlossaryKind }): Promise<GlossaryTermRecord> => request("POST", "/glossary", input),
    deleteGlossaryTerm: (key: string): Promise<{ key: string }> => request("DELETE", `/glossary/${encodeURIComponent(key)}`),
    /** Lage des Bestands (Vorstand, Geschäftsstelle). */
    getDashboard: (): Promise<GuidelineDashboard> => request("GET", "/dashboard"),
    /** Neue Richtlinie ohne Datei: Standardgerüst oder bestehende Richtlinie (Gliederung oder mit Inhalt). */
    createGuideline: (input: { number: string; title: string; workingGroup?: string | null; template: NewGuidelineTemplate; from?: string | null }): Promise<{ documentId: string; title: string; sections: number }> =>
      request("POST", "", input),
    getWorkspace: (documentId: string): Promise<GuidelineWorkspace> =>
      request("GET", `/${encodeURIComponent(documentId)}`),
    setLinkStatus: (documentId: string, linkId: string, status: LinkStatus): Promise<{ _id: string; status: LinkStatus }> =>
      request("PATCH", `/${encodeURIComponent(documentId)}/links/${encodeURIComponent(linkId)}`, { status }),
    saveDraft: (
      documentId: string,
      sectionId: string,
      text: string,
      /** Nur für neu angelegte Abschnitte: neuer Titel. */
      title?: string,
      /** Plate-Inhalt mit Kommentar- und Vorschlagsmarken und die Zahl offener Vorschläge. */
      value?: unknown[],
      openSuggestions?: number,
      /** Abschnitt mit Blockmodell: Blöcke statt Markdown, das Markdown erzeugt der Server. */
      content?: SectionContent | null,
    ): Promise<{ _id: string; title: string; draft: GuidelineSection["draft"]; requirements: GuidelineSection["requirements"] }> =>
      request("PATCH", `/${encodeURIComponent(documentId)}/sections/${encodeURIComponent(sectionId)}/draft`, {
        text,
        title,
        value,
        openSuggestions,
        ...(content ? { blocks: content.blocks, footnotes: content.footnotes ?? [] } : {}),
      }),
    listVersions: (documentId: string, sectionId: string): Promise<SectionVersions> =>
      request("GET", `/${encodeURIComponent(documentId)}/sections/${encodeURIComponent(sectionId)}/versions`),
    getVersion: (documentId: string, sectionId: string, rev: number): Promise<{ rev: number; text: string }> =>
      request("GET", `/${encodeURIComponent(documentId)}/sections/${encodeURIComponent(sectionId)}/versions/${rev}`),
    labelVersion: (documentId: string, sectionId: string, rev: number, label: string): Promise<{ rev: number; label: string | null }> =>
      request("PATCH", `/${encodeURIComponent(documentId)}/sections/${encodeURIComponent(sectionId)}/versions/${rev}`, { label }),
    createVersion: (documentId: string, sectionId: string, label: string): Promise<{ rev: number; label: string; created: boolean }> =>
      request("POST", `/${encodeURIComponent(documentId)}/sections/${encodeURIComponent(sectionId)}/versions`, { label }),
    restoreVersion: (documentId: string, sectionId: string, rev: number): Promise<{ rev: number; draft: GuidelineSection["draft"]; requirements: GuidelineSection["requirements"] }> =>
      request("POST", `/${encodeURIComponent(documentId)}/sections/${encodeURIComponent(sectionId)}/versions/${rev}/restore`),
    listRequirements: (documentId: string, sectionId: string): Promise<SectionRequirements> =>
      request("GET", `/${encodeURIComponent(documentId)}/sections/${encodeURIComponent(sectionId)}/requirements`),
    updateRequirement: (documentId: string, key: string, patch: Partial<Pick<StoredRequirement, "status" | "addressee" | "roleCode" | "subject" | "action" | "interval" | "obligationType">>): Promise<{ key: string; status: string }> =>
      request("PATCH", `/${encodeURIComponent(documentId)}/requirements/${encodeURIComponent(key)}`, patch),
    /** „Abschnitt entwerfen“: Modellentwurf als Änderungsvorschläge in den Arbeitsstand (Autor, Leitung). */
    proposeSectionDraft: (documentId: string, sectionId: string): Promise<SectionDraftProposal> =>
      request("POST", `/${encodeURIComponent(documentId)}/sections/${encodeURIComponent(sectionId)}/draft-proposal`, {}),
    analyzeRequirements: (documentId: string, sectionId: string, force = false): Promise<{ created: number; changed: number; retired: number; kept: number; decomposed: number }> =>
      request("POST", `/${encodeURIComponent(documentId)}/sections/${encodeURIComponent(sectionId)}/requirements/analyze`, { force }),
    getSectionContent: (documentId: string, sectionId: string): Promise<SectionBlocks> =>
      request("GET", `/${encodeURIComponent(documentId)}/sections/${encodeURIComponent(sectionId)}/content`),
    listDiscussions: async (documentId: string): Promise<Discussion[]> =>
      (await request("GET", `/${encodeURIComponent(documentId)}/discussions`))?.items || [],
    createDiscussion: (documentId: string, section: string, discussionId: string, quote: string, body: string, element?: string | null): Promise<Discussion> =>
      request("POST", `/${encodeURIComponent(documentId)}/discussions`, { section, discussionId, quote, body, element }),
    replyDiscussion: (documentId: string, discussionId: string, body: string): Promise<Discussion> =>
      request("POST", `/${encodeURIComponent(documentId)}/discussions/${encodeURIComponent(discussionId)}/comments`, { body }),
    resolveDiscussion: (documentId: string, discussionId: string, resolved: boolean): Promise<Discussion> =>
      request("PATCH", `/${encodeURIComponent(documentId)}/discussions/${encodeURIComponent(discussionId)}`, { resolved }),
    createSection: (
      documentId: string,
      anchor: string,
      position: "after" | "child",
      title: string,
    ): Promise<GuidelineSection> =>
      request("POST", `/${encodeURIComponent(documentId)}/sections`, { anchor, position, title }),
    setRemoved: (documentId: string, sectionId: string, removed: boolean): Promise<{ removed: boolean; deleted: string[] }> =>
      request("PATCH", `/${encodeURIComponent(documentId)}/sections/${encodeURIComponent(sectionId)}/removed`, { removed }),
    /** Das Original-PDF als Bytes für pdf.js. */
    /** Wortlaut und amtliche Seite einer Vorschrift, auf die das Dokument verweist. */
    getProvision: (documentId: string, regulationId: string, provisionId?: string | null): Promise<ProvisionView> =>
      request(
        "GET",
        `/${encodeURIComponent(documentId)}/provision?regulationId=${encodeURIComponent(regulationId)}${provisionId ? `&provisionId=${encodeURIComponent(provisionId)}` : ""}`,
      ),
    getSectionStructure: (documentId: string, sectionId: string): Promise<SectionStructure> =>
      request("GET", `/${encodeURIComponent(documentId)}/sections/${encodeURIComponent(sectionId)}/sts`),
    getPdf: async (documentId: string): Promise<ArrayBuffer> => {
      const response = await authenticatedFetch(`${baseUrl}/${encodeURIComponent(documentId)}/pdf`, { method: "GET" });
      if (!response.ok) await processResponse(response);
      return response.arrayBuffer();
    },
    /** Was in den Richtlinien auf den Nutzer wartet. */
    getInbox: async (): Promise<InboxItem[]> => (await request("GET", "/inbox"))?.items || [],
    /** Suche über alle sichtbaren Richtlinien (Vorauswahl auf dem Server). */
    searchAll: async (q: string): Promise<{ sections: GlobalSearchSection[]; truncated: boolean }> => {
      const r = await request("GET", `/search?q=${encodeURIComponent(q)}`);
      return { sections: r?.sections || [], truncated: !!r?.truncated };
    },
    /** Ausgabedatei aus dem Blockmodell (Word, PDF, NISO-STS) mit Dateinamen vom Server. */
    exportDocument: async (documentId: string, format: ExportFormat): Promise<{ blob: Blob; name: string }> => {
      const response = await authenticatedFetch(`${baseUrl}/${encodeURIComponent(documentId)}/export?format=${format}`, { method: "GET" });
      if (!response.ok) await processResponse(response);
      const disposition = response.headers.get("Content-Disposition") ?? "";
      const encoded = disposition.match(/filename\*=UTF-8''([^;]+)/)?.[1];
      return { blob: await response.blob(), name: encoded ? decodeURIComponent(encoded) : `richtlinie.${format === "sts" ? "xml" : format}` };
    },
    /** Neue Richtlinie aus einer Word-Datei; ohne `apply` nur Probelauf (was erkannt würde). */
    importDocx: async (file: File, apply: boolean, title?: string): Promise<DocxImportResult> => {
      const form = new FormData();
      form.append("file", file);
      form.append("apply", String(apply));
      if (title) form.append("title", title);
      const response = await authenticatedFetch(`${baseUrl}/import`, { method: "POST", body: form, headers: {} });
      return processResponse(response);
    },
    getEdition: (documentId: string): Promise<EditionState> => request("GET", `/${encodeURIComponent(documentId)}/edition`),
    submitEdition: (documentId: string, label: string): Promise<EditionRecord> =>
      request("POST", `/${encodeURIComponent(documentId)}/editions`, { label }),
    withdrawEdition: (documentId: string, editionId: string): Promise<{ status: string }> =>
      request("POST", `/${encodeURIComponent(documentId)}/editions/${encodeURIComponent(editionId)}/withdraw`),
    decideEdition: (
      documentId: string,
      editionId: string,
      decision: "approve" | "reject",
      comment: string,
    ): Promise<{ status: string; newDocument?: string }> =>
      request("POST", `/${encodeURIComponent(documentId)}/editions/${encodeURIComponent(editionId)}/decision`, { decision, comment }),
    getReview: (documentId: string): Promise<{ markdown: string }> =>
      request("GET", `/${encodeURIComponent(documentId)}/review`),
  };
};
